import React, { useEffect, useState } from 'react';
import { Table, Card, Button, Spinner, Alert } from 'react-bootstrap';
import CustomPagination from './CustomPagination';
import axiosInstance from '../axios';
import { saveAs } from 'file-saver';
import moment from 'moment';

const UserFilesList = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadingFileId, setDownloadingFileId] = useState(null);
  const [downloadSuccess, setDownloadSuccess] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    totalCount: 0
  });

  const fetchUserFiles = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosInstance.post('/account/files/list', {
        page: pagination.page,
        limit: pagination.limit
      });
      
      if (response.data.success) {
        setFiles(response.data.files || []);
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.totalCount || 0
        }));
      } else {
        setError('Failed to load files. Please try again later.');
      }
    } catch (err) {
      console.error('Error fetching user files:', err);
      setError('Failed to load files. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserFiles();
  }, [pagination.page, pagination.limit]);

  useEffect(() => {
    if (downloadSuccess) {
      const timer = setTimeout(() => {
        setDownloadSuccess(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [downloadSuccess]);

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const handleLimitChange = (e) => {
    setPagination(prev => ({ 
      ...prev, 
      limit: parseInt(e.target.value, 10),
      page: 1 // Reset to first page when changing limit
    }));
  };

  const handleDownload = async (fileId) => {
    setDownloadingFileId(fileId);
    setError(null);
    
    try {
      const response = await axiosInstance.post('/account/files/download', {
        files: [{ ID: fileId }]
      });
      
      if (response.data.success && response.data.files && response.data.files.length > 0) {
        const fileData = response.data.files[0];
        const fileUrl = fileData.url;
        
        if (fileUrl) {
          // Fetch the actual file from the provided URL
          const fileResponse = await fetch(fileUrl);
          const blob = await fileResponse.blob();
          
          // Save the file using file-saver
          saveAs(blob, fileData.ORIGINAL_FILE_NAME);
          setDownloadSuccess(`Successfully downloaded "${fileData.ORIGINAL_FILE_NAME}"`);
        } else {
          throw new Error('Download URL not found in response');
        }
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download the file. Please try again later.');
    } finally {
      setDownloadingFileId(null);
    }
  };

  const renderDate = (date) => {
    return date && moment(date).isValid() ? moment(date).format('MM-DD-YYYY') : '';
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop().toLowerCase();
  };

  const getFileIcon = (filename) => {
    const ext = getFileExtension(filename);
    switch (ext) {
      case 'pdf':
        return 'bi-file-pdf';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'bi-file-image';
      case 'doc':
      case 'docx':
        return 'bi-file-word';
      case 'xls':
      case 'xlsx':
        return 'bi-file-excel';
      default:
        return 'bi-file-earmark';
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="bg-light d-flex justify-content-between align-items-center">
        <h5 className="mb-0">User Documents</h5>
        <div className="text-muted small">Total: {pagination.totalCount} document(s)</div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {downloadSuccess && <Alert variant="success">{downloadSuccess}</Alert>}
        
        <Table responsive striped hover className="mt-2">
          <thead>
            <tr>
              <th className="tableheader">File Name</th>
              <th className="tableheader">Owner #</th>
              <th className="tableheader">Tax ID</th>
              <th className="tableheader">Date Uploaded</th>
              <th className="tableheader">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </td>
              </tr>
            ) : files.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-3">
                  No files found
                </td>
              </tr>
            ) : (
              files.map((file) => (
                <tr key={file.ID}>
                  <td>
                    <div className="d-flex align-items-center">
                      <i className={`bi ${getFileIcon(file.ORIGINAL_FILE_NAME)} me-2 text-primary`}></i>
                      <span className="text-truncate" style={{ maxWidth: "250px" }}>
                        {file.ORIGINAL_FILE_NAME}
                      </span>
                    </div>
                  </td>
                  <td>{file.OWNER_NUMBER}</td>
                  <td>{file.PAYMENT_TAXID}</td>
                  <td>{renderDate(file.CREATED_ON)}</td>
                  <td>
                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => handleDownload(file.ID)}
                      disabled={downloadingFileId === file.ID}
                    >
                      {downloadingFileId === file.ID ? (
                        <>
                          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                          Downloading...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-download me-1"></i>
                          Download
                        </>
                      )}
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        
        {!loading && files.length > 0 && (
          <CustomPagination
            page={pagination.page}
            limit={pagination.limit}
            total={pagination.totalCount}
            onPageChange={handlePageChange}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card.Body>
    </Card>
  );
};

export default UserFilesList; 